import {useState} from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Lock,
  MoreHorizontal,
  Pencil,
  PowerOff,
  Send,
  ToggleRight,
  Trash2
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Spinner,
  Switch,
  Textarea,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {
  useApproveSwitchProposal,
  useKillSwitch,
  useProposeSwitchChange,
  useRejectSwitchProposal,
  useSwitchHistory,
  useSwitchList,
  useSwitchProposals,
  useUsers
} from '@/api/hooks';
import {isSwitchConflict, type SwitchProposal, type SwitchView} from '@/api/switches';
import {type Role} from '@/api/users';
import {KILL_SWITCHES_PERMISSION, WRITE_SWITCHES_PERMISSION, useAuth} from '@/context/useAuth';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {toneBadge} from '@/lib/status';
import {showToast} from '@/lib/toast';

type ReasonKind = 'kill' | 'clear' | 'delete';

type Action =
  | {readonly kind: ReasonKind; readonly item: SwitchView}
  | {readonly kind: 'targeting'; readonly item: SwitchView};

// Switch, owner, lifetime, targeting, on/off and the actions menu.
const SWITCHES_COLUMN_COUNT = 6;
// When, actor, change and reason.
const HISTORY_COLUMN_COUNT = 4;

// Switchboard matches a role by name, so the editor offers PRI's own roles verbatim.
const TARGET_ROLES: readonly Role[] = ['analyst', 'analyst_admin', 'admin'];

const CHANGE_LABEL: Record<SwitchProposal['kind'], string> = {
  targeting: 'Targeting change',
  clear: 'Turn back on',
  delete: 'Deletion'
};

const REASON_COPY: Record<
  ReasonKind,
  {title: string; description: string; confirm: string; done: (key: string) => string}
> = {
  kill: {
    title: 'Kill',
    description: 'Turns the capability off for everyone, whatever its targeting. Takes effect now.',
    confirm: 'Turn off now',
    done: (key) => `Killed ${key}`
  },
  clear: {
    title: 'Propose turning back on',
    description:
      'Turns the capability back on, for what its targeting selects, once an approver approves it.',
    confirm: 'Submit for review',
    done: (key) => `Submitted turning ${key} back on for review`
  },
  delete: {
    title: 'Propose deleting',
    description:
      'Removes the capability, which has left the manifest, once an approver approves it.',
    confirm: 'Submit for review',
    done: (key) => `Submitted deleting ${key} for review`
  }
};

const PERCENTAGE_COPY: Record<'item' | 'user' | 'unknown', {label: string; help: string}> = {
  item: {
    label: 'Share of items (%)',
    help: 'Each item is in or out by a hash of its ID, so the same item always gets the same answer. Not tied to users or traffic volume.'
  },
  user: {
    label: 'Share of other users (%)',
    help: 'Listed users and roles always get it; everyone else is bucketed by user.'
  },
  unknown: {
    label: 'Share of checks (%)',
    help: 'Listed users and roles always get it; every other check is bucketed by the ID it sends.'
  }
};

function percentageNoun(item: SwitchView): string {
  return item.checked_per === 'item' ? 'items' : item.checked_per === 'user' ? 'users' : 'checks';
}

function targetingSummary(item: SwitchView): string {
  const parts = [`${String(item.percentage ?? 0)}% of ${percentageNoun(item)}`];
  if (item.checked_per !== 'item') {
    const lists: Array<[string[] | null, string]> = [
      [item.users, 'user'],
      [item.roles, 'role']
    ];
    for (const [list, noun] of lists) {
      const count = list?.length ?? 0;
      if (count > 0) {
        parts.push(`${String(count)} ${noun}${count === 1 ? '' : 's'}`);
      }
    }
  }

  return parts.join(' · ');
}

function editable(item: SwitchView): boolean {
  return !item.custom && item.variants === null;
}

function failureTone(error: unknown): 'warning' | 'error' {
  return isSwitchConflict(error) ? 'warning' : 'error';
}

// Off for every item or user: killed, or plain targeting that selects nobody.
function isOff(item: SwitchView): boolean {
  return (
    item.state === 'killed' ||
    (!item.custom &&
      item.variants === null &&
      item.percentage === 0 &&
      (item.users ?? []).length === 0 &&
      (item.roles ?? []).length === 0)
  );
}

type SwitchTreeRow = {
  item: SwitchView;
  depth: number;
  hasChildren: boolean;
  inactiveBy: string | null;
};

// Each switch followed by its children, depth-first, omitting the children of collapsed switches.
// A switch whose parent isn't listed is a root.
function switchTree(items: readonly SwitchView[], collapsed: ReadonlySet<string>): SwitchTreeRow[] {
  const keys = new Set(items.map((item) => item.key));
  const rows: SwitchTreeRow[] = [];
  const visit = (item: SwitchView, depth: number, inactiveBy: string | null) => {
    const children = items.filter((child) => child.parent === item.key);
    rows.push({item, depth, hasChildren: children.length > 0, inactiveBy});
    if (collapsed.has(item.key)) {
      return;
    }

    const childInactiveBy = inactiveBy ?? (isOff(item) ? item.key : null);
    for (const child of children) {
      visit(child, depth + 1, childInactiveBy);
    }
  };

  for (const item of items) {
    if (item.parent === null || !keys.has(item.parent)) {
      visit(item, 0, null);
    }
  }

  return rows;
}

export function SwitchesAdmin() {
  const {user} = useAuth();
  const permissions = user?.permissions ?? [];
  const canWrite = permissions.includes(WRITE_SWITCHES_PERMISSION);
  const canKill = permissions.includes(KILL_SWITCHES_PERMISSION);
  const {data, isLoading, error} = useSwitchList();
  const {data: proposals = []} = useSwitchProposals();
  const [action, setAction] = useState<Action>();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const toggleCollapsed = (key: string) => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) {
        next.add(key);
      }

      return next;
    });
  };

  const close = () => {
    setAction(undefined);
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>Failed to load capabilities</AlertTitle>
        <AlertDescription>{getApiErrorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  const switches = data?.switches ?? [];
  const pending = proposals.filter((proposal) => proposal.status === 'pending');
  const pendingKeys = new Set(
    pending.flatMap((proposal) => proposal.changes.map((change) => change.key))
  );

  return (
    <>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Capability</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Lifetime</TableHead>
              <TableHead>Targeting</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="w-6">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={SWITCHES_COLUMN_COUNT}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : switches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={SWITCHES_COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <ToggleRight />
                      </EmptyMedia>
                      <EmptyTitle>No capabilities</EmptyTitle>
                      <EmptyDescription>No product has declared a capability yet.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              switchTree(switches, collapsed).map(({item, depth, hasChildren, inactiveBy}) => (
                <SwitchRow
                  key={item.key}
                  item={item}
                  depth={depth}
                  expanded={hasChildren ? !collapsed.has(item.key) : null}
                  inactiveBy={inactiveBy}
                  hasPending={pendingKeys.has(item.key)}
                  canWrite={canWrite}
                  canKill={canKill}
                  onToggleExpanded={() => {
                    toggleCollapsed(item.key);
                  }}
                  onAction={setAction}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TablePanel>

      <PendingReview proposals={pending} switches={switches} canWrite={canWrite} />

      <SwitchHistory />

      <Reviews proposals={proposals.filter((proposal) => proposal.status !== 'pending')} />

      {action && action.kind !== 'targeting' ? (
        <ReasonDialog kind={action.kind} item={action.item} onClose={close} />
      ) : null}
      {action?.kind === 'targeting' ? (
        <TargetingDialog
          item={action.item}
          switches={switches}
          pendingKeys={pendingKeys}
          onClose={close}
        />
      ) : null}
    </>
  );
}

function SwitchRow({
  item,
  depth,
  expanded,
  onToggleExpanded,
  inactiveBy,
  hasPending,
  canWrite,
  canKill,
  onAction
}: {
  readonly item: SwitchView;
  readonly depth: number;
  /** Null for a switch with no children. */
  readonly expanded: boolean | null;
  readonly onToggleExpanded: () => void;
  readonly inactiveBy: string | null;
  readonly hasPending: boolean;
  readonly canWrite: boolean;
  readonly canKill: boolean;
  readonly onAction: (action: Action) => void;
}) {
  const enabled = item.state === 'enabled';
  // The kill:switches permission reaches only the switches the manifest marks as kill switches.
  // Turning a switch back on, like every other change, is a proposal and needs write:switches.
  const canPropose = canWrite && !hasPending;
  const canToggle = enabled ? canWrite || (canKill && item.kill_switch) : canPropose;
  const canEditTargeting = canPropose && editable(item);
  const canDelete = canPropose && !item.in_manifest;

  return (
    <TableRow>
      <TableCell className="min-w-64 whitespace-normal">
        <div className="flex items-start gap-1" style={{paddingLeft: `${depth * 1.75}rem`}}>
          {expanded === null ? (
            <div className="size-7 shrink-0" />
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-expanded={expanded}
              aria-label={expanded ? `Collapse ${item.key}` : `Expand ${item.key}`}
              onClick={onToggleExpanded}>
              {expanded ? <ChevronDown /> : <ChevronRight />}
            </Button>
          )}
          <div className="min-w-0 py-1">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs font-medium">{item.key}</span>
              {item.in_manifest ? null : <Badge variant="outline">Not in manifest</Badge>}
              {inactiveBy ? (
                <Badge {...toneBadge('destructive')}>Inactive — {inactiveBy} is off</Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-xs">{item.description || '—'}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">{item.owner || '—'}</TableCell>
      <TableCell className="whitespace-nowrap">
        {item.permanent ? (
          <span className="text-muted-foreground">Permanent</span>
        ) : item.remove_by ? (
          <span className="text-muted-foreground">Remove by {item.remove_by}</span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {editable(item) ? targetingSummary(item) : <Badge variant="outline">Custom</Badge>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            size="sm"
            checked={enabled}
            disabled={!canToggle}
            aria-label={`Enable ${item.key}`}
            onCheckedChange={(checked) => {
              onAction({kind: checked ? 'clear' : 'kill', item});
            }}
          />
          {enabled ? (
            <span className="text-muted-foreground text-xs">
              {isOff(item) ? 'On, but targeting selects nobody' : 'On, per targeting'}
            </span>
          ) : (
            <Badge {...toneBadge('destructive')}>Killed</Badge>
          )}
          {hasPending ? <Badge {...toneBadge('warning')}>Change pending</Badge> : null}
        </div>
      </TableCell>
      <TableCell>
        {/* Leave the menu out rather than offer it empty. */}
        {canEditTargeting || canDelete ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${item.key}`} />
              }>
              <MoreHorizontal />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEditTargeting ? (
                <DropdownMenuItem
                  onClick={() => {
                    onAction({kind: 'targeting', item});
                  }}>
                  <Pencil />
                  Propose targeting change
                </DropdownMenuItem>
              ) : null}
              {canDelete ? (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => {
                    onAction({kind: 'delete', item});
                  }}>
                  <Trash2 />
                  Propose deletion
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function PendingReview({
  proposals,
  switches,
  canWrite
}: {
  readonly proposals: SwitchProposal[];
  readonly switches: SwitchView[];
  readonly canWrite: boolean;
}) {
  const {user} = useAuth();
  const {data: users = []} = useUsers();
  const nameOf = (subject: string) =>
    users.find((u) => u.auth0_subject === subject)?.display_name ?? subject;
  // The same rule as workflow proposals: an author may decide their own proposal only as an admin.
  const canDecide = (author: string) =>
    user?.role === 'admin' || author.toLowerCase() !== user?.username?.toLowerCase();

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-medium">Pending review</h3>
        <p className="text-muted-foreground text-xs">
          Kills apply at once. Every other change waits here until an approver decides.
        </p>
      </div>
      {proposals.length === 0 ? (
        <Empty className="border-border border p-6">
          <EmptyHeader>
            <EmptyTitle>Nothing to review</EmptyTitle>
            <EmptyDescription>
              Proposed switch changes wait here until an approver decides.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        proposals.map((proposal) => (
          <ProposalReview
            key={proposal.id}
            proposal={proposal}
            switches={switches}
            allowed={canWrite ? canDecide(proposal.author) : false}
            canReview={canWrite}
            nameOf={nameOf}
          />
        ))
      )}
    </div>
  );
}

function ProposalReview({
  proposal,
  switches,
  allowed,
  canReview,
  nameOf
}: {
  readonly proposal: SwitchProposal;
  readonly switches: readonly SwitchView[];
  readonly allowed: boolean;
  readonly canReview: boolean;
  readonly nameOf: (subject: string) => string;
}) {
  const approve = useApproveSwitchProposal();
  const reject = useRejectSwitchProposal();
  const [ask, setAsk] = useState<'approve' | 'reject' | null>(null);
  const busy = approve.isPending || reject.isPending;
  const key = proposal.switch_key;

  const decide = async (decision: 'approve' | 'reject') => {
    try {
      await (decision === 'approve' ? approve : reject).mutateAsync({id: proposal.id});
      showToast(
        decision === 'approve'
          ? `Approved and applied the change to ${key}`
          : `Rejected the change to ${key}`
      );
    } catch (error) {
      showToast(
        getApiErrorMessage(error, `Couldn't ${decision} the change to ${key}.`),
        failureTone(error)
      );
    }
  };

  const list = (values: string[], render: (value: string) => string = (value) => value) =>
    values.length === 0 ? '—' : values.map(render).join(', ');
  const rows = proposal.changes.flatMap(({key: switchKey, targeting, base}) => {
    const byItem = switches.find((s) => s.key === switchKey)?.checked_per === 'item';
    const fields: Array<[string, string, string]> = [
      ['Percentage', `${String(base.percentage)}%`, `${String(targeting.percentage)}%`]
    ];
    if (!byItem) {
      fields.push(
        ['Users', list(base.users, nameOf), list(targeting.users, nameOf)],
        ['Roles', list(base.roles), list(targeting.roles)]
      );
    }

    return fields.map(([field, current, proposed]) => ({
      id: `${switchKey}/${field}`,
      switchKey,
      field,
      current,
      proposed
    }));
  });
  const destructive = proposal.kind === 'delete';

  return (
    <section className="border-border bg-card flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">
            {CHANGE_LABEL[proposal.kind]} · <span className="font-mono">{key}</span>
          </h4>
          <p className="text-muted-foreground text-xs">
            Submitted by {proposal.author} ·{' '}
            {new Date(proposal.submitted_at).toLocaleString('en-GB')}
          </p>
        </div>
        {allowed ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setAsk('reject');
              }}>
              Reject
            </Button>
            <Button
              size="sm"
              variant={destructive ? 'destructive' : 'default'}
              disabled={busy}
              onClick={() => {
                setAsk('approve');
              }}>
              {busy ? <Spinner /> : <Check aria-hidden className="size-4" />}
              Approve
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-sm">
            <Lock aria-hidden className="size-3" />
            {canReview ? 'Waiting for an independent approver.' : 'Waiting for an approver'}
          </span>
        )}
      </div>
      <p className="text-sm">{proposal.reason}</p>
      {proposal.kind === 'clear' ? (
        <p className="text-muted-foreground text-sm">
          Turns <span className="font-mono">{key}</span> back on, for what its targeting selects.
        </p>
      ) : null}
      {proposal.kind === 'delete' ? (
        <p className="text-muted-foreground text-sm">
          Removes <span className="font-mono">{key}</span> from the capabilities.
        </p>
      ) : null}
      {rows.length > 0 ? (
        <div className="border-border overflow-x-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Switch</TableHead>
                <TableHead className="w-32">Field</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Proposed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({id, switchKey, field, current, proposed}) => (
                <TableRow key={id}>
                  <TableCell className="font-mono text-xs">{switchKey}</TableCell>
                  <TableCell className="font-medium">{field}</TableCell>
                  <TableCell className="whitespace-normal">{current}</TableCell>
                  <TableCell className="whitespace-normal">{proposed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
      <AlertDialog
        open={ask !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAsk(null);
          }
        }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {ask === 'reject' ? 'Reject this change?' : `Approve this change to ${key}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {ask === 'reject'
                ? 'Nothing about the capability changes. The author can propose again.'
                : 'It is applied now, and every service picks it up within seconds.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={ask === 'reject' || destructive ? 'destructive' : 'default'}
              onClick={() => {
                if (ask) {
                  void decide(ask);
                }

                setAsk(null);
              }}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function SwitchHistory() {
  const {data = [], isLoading, error} = useSwitchHistory(true);
  const {data: users = []} = useUsers();
  // Switchboard-api records the token subject; "manifest" marks a deploy's reconcile.
  const actorName = (actor: string) =>
    users.find((u) => u.auth0_subject === actor)?.display_name ?? actor;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-medium">Recent changes</h3>
        <p className="text-muted-foreground text-xs">
          Every change to the switch document, with who made it and why.
        </p>
      </div>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={HISTORY_COLUMN_COUNT}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={HISTORY_COLUMN_COUNT}
                  className="text-destructive-ink py-4 text-center text-xs">
                  {getApiErrorMessage(error, "Couldn't load the change history.")}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HISTORY_COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyTitle>No changes yet</EmptyTitle>
                      <EmptyDescription>
                        Nobody has changed a switch since the document was created.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              data.map((version) => (
                <TableRow key={version.generation}>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {new Date(version.updated_at).toLocaleString('en-GB')}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{actorName(version.actor)}</TableCell>
                  <TableCell className="min-w-64 whitespace-normal">
                    {version.summary ?? '—'}
                  </TableCell>
                  <TableCell className="min-w-64 whitespace-normal">
                    {version.reason ?? '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TablePanel>
    </div>
  );
}

function Reviews({proposals}: {readonly proposals: SwitchProposal[]}) {
  if (proposals.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-medium">Reviews</h3>
        <p className="text-muted-foreground text-xs">Decided proposals, newest first.</p>
      </div>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Capability</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Decided by</TableHead>
              <TableHead>When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {proposals.map((proposal) => (
              <TableRow key={proposal.id}>
                <TableCell className="font-mono text-xs">{proposal.switch_key}</TableCell>
                <TableCell className="whitespace-nowrap">{CHANGE_LABEL[proposal.kind]}</TableCell>
                <TableCell className="whitespace-nowrap">{proposal.author}</TableCell>
                <TableCell>
                  <Badge {...toneBadge(proposal.status === 'approved' ? 'success' : 'destructive')}>
                    {proposal.status === 'approved' ? 'Approved' : 'Rejected'}
                  </Badge>
                </TableCell>
                <TableCell className="whitespace-nowrap">{proposal.decided_by ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {proposal.decided_at
                    ? new Date(proposal.decided_at).toLocaleString('en-GB')
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TablePanel>
    </div>
  );
}

function ReasonField({
  value,
  onChange
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor="switch-reason">Reason</FieldLabel>
      <Textarea
        id="switch-reason"
        value={value}
        maxLength={500}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <FieldDescription>Required. Recorded in the change history.</FieldDescription>
    </Field>
  );
}

function ReasonDialog({
  kind,
  item,
  onClose
}: {
  readonly kind: ReasonKind;
  readonly item: SwitchView;
  readonly onClose: () => void;
}) {
  const kill = useKillSwitch();
  const propose = useProposeSwitchChange();
  const pendingWrite = kind === 'kill' ? kill.isPending : propose.isPending;
  const copy = REASON_COPY[kind];
  const [reason, setReason] = useState('');
  const Icon = kind === 'kill' ? PowerOff : Send;

  const submit = async () => {
    try {
      await (kind === 'kill'
        ? kill.mutateAsync({key: item.key, reason: reason.trim()})
        : propose.mutateAsync({key: item.key, payload: {kind, reason: reason.trim()}}));
      showToast(copy.done(item.key));
      onClose();
    } catch (error) {
      showToast(getApiErrorMessage(error, `Couldn't change ${item.key}.`), failureTone(error));
      if (isSwitchConflict(error)) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {copy.title} <span className="font-mono">{item.key}</span>?
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <ReasonField value={reason} onChange={setReason} />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={kind === 'clear' ? 'default' : 'destructive'}
            disabled={reason.trim() === '' || pendingWrite}
            onClick={() => {
              void submit();
            }}>
            {pendingWrite ? <Spinner /> : <Icon />}
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type TargetingDraft = {users: string[]; roles: string[]; percentage: string};

const draftOf = (item: SwitchView): TargetingDraft => ({
  users: item.users ?? [],
  roles: item.roles ?? [],
  percentage: String(item.percentage ?? 0)
});

const percentOf = (draft: TargetingDraft) => Number(draft.percentage);

const percentValid = (draft: TargetingDraft) => {
  const percent = percentOf(draft);
  return draft.percentage !== '' && Number.isInteger(percent) && percent >= 0 && percent <= 100;
};

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

// Item-checked switches have no users or roles, so only their percentage can differ.
function changedFrom(item: SwitchView, draft: TargetingDraft): boolean {
  return (
    percentOf(draft) !== (item.percentage ?? 0) ||
    (item.checked_per !== 'item' &&
      (!sameSet(draft.users, item.users ?? []) || !sameSet(draft.roles, item.roles ?? [])))
  );
}

function descendantsOf(root: SwitchView, switches: readonly SwitchView[]) {
  const rows: Array<{item: SwitchView; depth: number}> = [];
  const visit = (parent: string, depth: number) => {
    for (const child of switches.filter((s) => s.parent === parent)) {
      rows.push({item: child, depth});
      visit(child.key, depth + 1);
    }
  };

  visit(root.key, 1);
  return rows;
}

function TargetingFields({
  item,
  draft,
  onChange
}: {
  readonly item: SwitchView;
  readonly draft: TargetingDraft;
  readonly onChange: (draft: TargetingDraft) => void;
}) {
  const {data: users = []} = useUsers();
  const byItem = item.checked_per === 'item';
  const [search, setSearch] = useState('');
  const percentCopy = PERCENTAGE_COPY[item.checked_per ?? 'unknown'];
  const id = `switch-${item.key}`;

  // A role another product targeted stays offered, so a proposal never drops it silently.
  const roleOptions = [
    ...TARGET_ROLES,
    ...draft.roles.filter((r) => !TARGET_ROLES.includes(r as Role))
  ];

  const needle = search.trim().toLowerCase();
  const candidates = users
    .filter(
      (u) =>
        u.auth0_subject !== null &&
        !draft.users.includes(u.auth0_subject) &&
        needle !== '' &&
        `${u.display_name} ${u.email}`.toLowerCase().includes(needle)
    )
    .slice(0, 8);
  const nameOf = (subject: string) => {
    const match = users.find((u) => u.auth0_subject === subject);
    return match ? `${match.display_name} (${match.email})` : subject;
  };

  return (
    <>
      {byItem ? null : (
        <>
          <Field>
            <FieldLabel htmlFor={`${id}-user-search`}>Users</FieldLabel>
            {draft.users.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {draft.users.map((subject) => (
                  <Button
                    key={subject}
                    type="button"
                    size="xs"
                    variant="secondary"
                    aria-label={`Remove ${nameOf(subject)}`}
                    onClick={() => {
                      onChange({...draft, users: draft.users.filter((s) => s !== subject)});
                    }}>
                    {nameOf(subject)} ×
                  </Button>
                ))}
              </div>
            ) : null}
            <Input
              id={`${id}-user-search`}
              placeholder="Search by name or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {candidates.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {candidates.map((u) => (
                  <Button
                    key={u.id}
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      onChange({...draft, users: [...draft.users, u.auth0_subject ?? '']});
                      setSearch('');
                    }}>
                    {u.display_name} ({u.email})
                  </Button>
                ))}
              </div>
            ) : null}
          </Field>
          <FieldSet className="gap-2">
            <FieldLegend variant="label" className="mb-0">
              Roles
            </FieldLegend>
            <ToggleGroup
              multiple
              variant="outline"
              spacing={0}
              size="sm"
              className="flex-wrap"
              aria-label="Roles"
              value={draft.roles}
              onValueChange={(next) => {
                onChange({...draft, roles: next});
              }}>
              {roleOptions.map((role) => (
                <ToggleGroupItem key={role} value={role}>
                  {role}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </FieldSet>
        </>
      )}
      <Field>
        <FieldLabel htmlFor={`${id}-percentage`}>{percentCopy.label}</FieldLabel>
        <Input
          id={`${id}-percentage`}
          type="number"
          min={0}
          max={100}
          step={1}
          value={draft.percentage}
          onChange={(event) => {
            onChange({...draft, percentage: event.target.value});
          }}
        />
        <FieldDescription>{percentCopy.help} Whole number, 0 to 100.</FieldDescription>
      </Field>
    </>
  );
}

function TargetingDialog({
  item,
  switches,
  pendingKeys,
  onClose
}: {
  readonly item: SwitchView;
  readonly switches: readonly SwitchView[];
  readonly pendingKeys: ReadonlySet<string>;
  readonly onClose: () => void;
}) {
  const propose = useProposeSwitchChange();
  const rows = [{item, depth: 0}, ...descendantsOf(item, switches)];
  const [drafts, setDrafts] = useState<Record<string, TargetingDraft>>(() =>
    Object.fromEntries(rows.map((row) => [row.item.key, draftOf(row.item)]))
  );
  const [reason, setReason] = useState('');
  const grouped = rows.length > 1;

  const isEditable = (sw: SwitchView) => editable(sw) && !pendingKeys.has(sw.key);
  const editableRows = rows.filter((row) => isEditable(row.item));
  const changed = editableRows.filter((row) => {
    const draft = drafts[row.item.key];
    return draft !== undefined && changedFrom(row.item, draft);
  });
  const allValid = editableRows.every((row) => {
    const draft = drafts[row.item.key];
    return draft !== undefined && percentValid(draft);
  });

  const submit = async () => {
    try {
      await propose.mutateAsync({
        key: item.key,
        payload: {
          kind: 'targeting',
          reason: reason.trim(),
          changes: changed.map(({item: sw}) => {
            const draft = drafts[sw.key]!;
            const byItem = sw.checked_per === 'item';
            return {
              key: sw.key,
              users: byItem ? [] : draft.users,
              roles: byItem ? [] : draft.roles,
              percentage: percentOf(draft)
            };
          })
        }
      });
      showToast(`Submitted targeting for ${item.key} for review`);
      onClose();
    } catch (error) {
      showToast(
        getApiErrorMessage(error, `Couldn't submit targeting for ${item.key}.`),
        failureTone(error)
      );
      if (isSwitchConflict(error)) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Targeting for <span className="font-mono">{item.key}</span>
          </DialogTitle>
          <DialogDescription>
            {item.checked_per === 'item'
              ? 'Checked per item, so only a share of items can be targeted.'
              : 'Listed users and roles always get the capability; the share below decides the rest.'}{' '}
            {grouped
              ? 'Every switch you change is proposed together and approved or rejected as one. '
              : ''}
            An approver has to approve the change before it takes effect.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-4">
          {rows.map(({item: sw, depth}) => (
            <section
              key={sw.key}
              aria-label={sw.key}
              className="flex flex-col gap-4"
              style={{marginLeft: `${String(depth)}rem`}}>
              {grouped ? <h4 className="font-mono text-sm font-semibold">{sw.key}</h4> : null}
              {pendingKeys.has(sw.key) ? (
                <Badge {...toneBadge('warning')}>Change pending</Badge>
              ) : editable(sw) ? (
                <TargetingFields
                  item={sw}
                  draft={drafts[sw.key]!}
                  onChange={(draft) => {
                    setDrafts((current) => ({...current, [sw.key]: draft}));
                  }}
                />
              ) : (
                <Badge variant="outline">Custom targeting, not editable here</Badge>
              )}
            </section>
          ))}
          <ReasonField value={reason} onChange={setReason} />
        </DialogBody>
        <DialogFooter sticky>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              reason.trim() === '' || changed.length === 0 || !allValid || propose.isPending
            }
            onClick={() => {
              void submit();
            }}>
            {propose.isPending ? <Spinner /> : <Send />}
            Submit for review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
